/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  Row,
  Table,
  Label,
  Card,
  CardBody,
  Input,
  FormGroup,
} from 'reactstrap';
import ReactSelect from 'react-select';

import { CompanyId } from '../../constants';
import LocalstorageService from '../../helpers/localstorage-service';
import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import reportService from '../../services/report';
import { exportToExcel, exportToPDF } from '../../helpers/exportUtils';
import NoRecords from '../../components/Table/NoRecords';

const LoadingGapReport = () => {
  const dispatch = useDispatch();
  const [setPageTitle, setCrumbItem] = useOutletContext();

  const companyId = LocalstorageService.getItem(CompanyId);
  const today = new Date();
  const threeMonthsAgo = new Date(today);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

  const fmtDate = (d) => d.toISOString().slice(0, 10); // YYYY-MM-DD

  // ── Filters ──
  const [fromDate, setFromDate] = useState(fmtDate(threeMonthsAgo));
  const [toDate, setToDate]     = useState(fmtDate(today));
  const [terminalNumber, setTerminalNumber] = useState('');
  const [gapType, setGapType]   = useState('-1'); // -1=הכל, 0=בין טעינות, 1=ספירה ידנית

  // Terminal selection
  const [terminalList, setTerminalList]     = useState([]);
  const [termOptList, setTermOptList]       = useState([]);
  const [selectedTerminal, setSelectedTerminal] = useState(null);

  // ── Results ──
  const [reportData, setReportData]   = useState([]);
  const [summary, setSummary]         = useState(null);
  const [isLoading, setIsLoading]     = useState(false);

  const gapTypeOptions = [
    { value: '-1', label: 'הכל' },
    { value: '0',  label: 'בין טעינות' },
    { value: '1',  label: 'ספירה ידנית' },
  ];

  useEffect(() => {
    setPageTitle('דוח פערים בין טעינות');
    setCrumbItem([
      { title: 'ATM', link: '#' },
      { title: 'דוחות', link: '#' },
      { title: 'פערים בין טעינות', link: '#' },
    ]);
    loadTerminals();
  }, []);

  const loadTerminals = async () => {
    dispatch(setLoadingOverlay(true));
    try {
      const response = await reportService.getTerminalsForReport({ companyId });
      const { status, dataList } = response.data;
      if (status === 200 && dataList?.length) {
        setTerminalList(dataList);
        setTermOptList(dataList.map((item) => ({
          value: item.terminalNumber,
          label: `${item.terminalNumber} – ${item.locationName}`,
        })));
      }
    } catch (e) {
      console.error('Error loading terminals:', e);
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  const handleTerminalChange = (opt) => {
    setSelectedTerminal(opt);
    setTerminalNumber(opt ? opt.value : '');
  };

  const generateReport = async () => {
    setIsLoading(true);
    dispatch(setLoadingOverlay(true));
    try {
      const payload = {
        companyId,
        fromDate,
        toDate,
        terminalNumber: terminalNumber || '',
        employeeId: 0,
        gapType: parseInt(gapType, 10),
      };

      const response = await reportService.getLoadingGapReport(payload);
      const { status, dataList, totalAbsGap, message } = response.data;

      if (status === 200) {
        setReportData(dataList || []);
        setSummary({ totalAbsGap: totalAbsGap || 0, count: (dataList || []).length });
        if (!dataList?.length) toast.info('לא נמצאו נתונים לפי הסינון שנבחר');
      } else {
        toast.error(message || 'שגיאה בטעינת הדוח');
      }
    } catch (e) {
      console.error('Error generating report:', e);
      toast.error('שגיאה בטעינת הדוח');
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const resetFilters = () => {
    setFromDate(fmtDate(threeMonthsAgo));
    setToDate(fmtDate(today));
    setTerminalNumber('');
    setSelectedTerminal(null);
    setGapType('-1');
    setReportData([]);
    setSummary(null);
  };

  const formatNum = (n) =>
    new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(n ?? 0);

  const gapTypeLabel = (t) => (t === 0 ? 'בין טעינות' : 'ספירה ידנית');

  const columns = [
    { key: 'rowNum',         header: '#' },
    { key: 'terminalNumber', header: 'מסוף' },
    { key: 'businessName',   header: 'מקום' },
    { key: 'employeeName',   header: 'עובד' },
    { key: 'batchStart',     header: 'אצווה מ' },
    { key: 'batchEnd',       header: 'אצווה עד' },
    { key: 'dateFrom',       header: 'מתאריך' },
    { key: 'dateTo',         header: 'עד תאריך' },
    { key: 'gapSign',        header: 'סוג פער' },
    { key: 'absGap',         header: 'פער (₪)' },
    { key: 'gapTypeLabel',   header: 'סוג בדיקה' },
  ];

  const handleExportExcel = () => {
    if (!reportData.length) { toast.warning('אין נתונים לייצוא'); return; }
    const rows = reportData.map((r, i) => ({ ...r, rowNum: i + 1, gapTypeLabel: gapTypeLabel(r.gapType) }));
    exportToExcel(rows, columns, `LoadingGapReport_${fmtDate(today)}`, 'דוח פערים בין טעינות');
    toast.success('הקובץ יורד בהצלחה');
  };

  const handleExportPDF = () => {
    if (!reportData.length) { toast.warning('אין נתונים לייצוא'); return; }
    const rows = reportData.map((r, i) => ({ ...r, rowNum: i + 1, gapTypeLabel: gapTypeLabel(r.gapType) }));
    exportToPDF(rows, columns, `LoadingGapReport_${fmtDate(today)}`, 'דוח פערים בין טעינות');
  };

  const selectStyles = {
    control: (p) => ({ ...p, minHeight: '38px' }),
    menu:    (p) => ({ ...p, zIndex: 9999 }),
    menuPortal: (p) => ({ ...p, zIndex: 9999 }),
  };

  return (
    <div className="loading-gap-report" dir="rtl">
      {/* ── Filters ── */}
      <Card className="mb-3">
        <CardBody>
          <h5 className="mb-3">🔍 סינון</h5>

          <Row className="mb-3">
            {/* Date range */}
            <Col md={3}>
              <Label>מתאריך</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </Col>
            <Col md={3}>
              <Label>עד תאריך</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </Col>

            {/* Terminal */}
            <Col md={4}>
              <Label>🏧 מסוף</Label>
              <ReactSelect
                value={selectedTerminal}
                onChange={handleTerminalChange}
                options={termOptList}
                styles={selectStyles}
                placeholder="-- כל המסופים --"
                isClearable
                isRtl
                noOptionsMessage={() => 'אין תוצאות'}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Col>

            {/* Gap type */}
            <Col md={2}>
              <Label>סוג פער</Label>
              <ReactSelect
                value={gapTypeOptions.find(o => o.value === gapType)}
                onChange={(opt) => setGapType(opt ? opt.value : '-1')}
                options={gapTypeOptions}
                styles={selectStyles}
                isRtl
              />
            </Col>
          </Row>

          <Row>
            <Col className="d-flex justify-content-end gap-2">
              <Button color="secondary" outline onClick={resetFilters}>🔄 איפוס</Button>
              <Button color="primary" onClick={generateReport} disabled={isLoading}>
                📊 הפקת דוח
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* ── Results ── */}
      {reportData.length > 0 && (
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">📋 תוצאות</h5>
              <div className="d-flex align-items-center gap-2">
                {summary && (
                  <span className="badge bg-danger fs-6 me-2">
                    סה"כ פער: {formatNum(summary.totalAbsGap)} ₪
                  </span>
                )}
                <Button color="success" size="sm" onClick={handleExportExcel}>📥 Excel</Button>
                <Button color="danger"  size="sm" onClick={handleExportPDF}>📄 PDF</Button>
              </div>
            </div>

            <div className="table-responsive">
              <Table bordered hover size="sm">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>מסוף</th>
                    <th>מקום</th>
                    <th>עובד</th>
                    <th>אצווה מ</th>
                    <th>אצווה עד</th>
                    <th>מתאריך</th>
                    <th>עד תאריך</th>
                    <th>חוסר/עודף</th>
                    <th>פער ₪</th>
                    <th>סוג</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, idx) => (
                    <tr key={row.id ?? idx}
                      className={row.gapSign === 'חוסר' ? 'table-danger' : 'table-warning'}>
                      <td>{idx + 1}</td>
                      <td>{row.terminalNumber}</td>
                      <td>{row.businessName}</td>
                      <td>{row.employeeName || '—'}</td>
                      <td>{row.batchStart || '—'}</td>
                      <td>{row.batchEnd   || '—'}</td>
                      <td>{row.dateFrom}</td>
                      <td>{row.dateTo}</td>
                      <td>
                        <span className={`badge ${row.gapSign === 'חוסר' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                          {row.gapSign}
                        </span>
                      </td>
                      <td className="fw-bold text-end">{formatNum(row.absGap)}</td>
                      <td>
                        <span className={`badge ${row.gapType === 0 ? 'bg-primary' : 'bg-info text-dark'}`}>
                          {gapTypeLabel(row.gapType)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-secondary fw-bold">
                    <td colSpan={9} className="text-end">סה"כ פער:</td>
                    <td className="text-end">{formatNum(summary?.totalAbsGap)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}

      {!isLoading && reportData.length === 0 && (
        <Card><CardBody><NoRecords /></CardBody></Card>
      )}
    </div>
  );
};

export default LoadingGapReport;
